import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import { RefreshCw, Users, HandHeart, Link2, AlertTriangle, MailX, Clock } from "lucide-react";

const REQ_STATUS = {
  open: "bg-amber-50 text-amber-800 border-amber-300",
  matching: "bg-amber-50 text-amber-800 border-amber-300",
  match_offered: "bg-blue-50 text-blue-800 border-blue-200",
  accepted: "bg-blue-50 text-blue-800 border-blue-300",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-300",
  expired: "bg-slate-100 text-slate-700 border-slate-300",
  admin_review: "bg-rose-50 text-rose-800 border-rose-300",
};

const Tile = ({ label, value, testId, icon: Icon, accent }) => (
  <Card><CardContent className="p-4">
    <div className="flex items-center gap-2 text-muted-foreground">
      {Icon && <Icon className={`h-4 w-4 ${accent || "text-primary"}`} />}
      <p className="text-[11px] font-semibold uppercase tracking-wider">{label}</p>
    </div>
    <p className="font-heading font-bold text-2xl tabular-nums mt-1.5" data-testid={testId}>{value}</p>
  </CardContent></Card>
);

export default function AdminWitnessNetwork() {
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(() => {
    adminApi.get("/admin/witness-network/summary").then((r) => setSummary(r.data)).catch(() => toast.error("Could not load summary"));
    adminApi.get("/admin/witness-network/volunteers").then((r) => setVolunteers(r.data)).catch(() => {});
  }, []);
  const loadRequests = useCallback(() => {
    const params = {};
    if (statusFilter !== "all") params.request_status = statusFilter;
    adminApi.get("/admin/witness-network/requests", { params }).then((r) => setRequests(r.data)).catch(() => {});
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const rb = summary?.requests_by_status || {};

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-network-title">Witness Network</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Witness requests, volunteer availability and matching health. The network is private — no public directory exists.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { load(); loadRequests(); }} data-testid="network-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
      </div>

      {!summary ? (
        <div className="grid sm:grid-cols-4 gap-3"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Tile label="Open / matching" value={(rb.open ?? 0) + (rb.matching ?? 0)} testId="nw-tile-open" icon={Clock} accent="text-amber-600" />
          <Tile label="Match offered" value={rb.match_offered ?? 0} testId="nw-tile-offered" icon={Link2} accent="text-blue-600" />
          <Tile label="Accepted" value={rb.accepted ?? 0} testId="nw-tile-accepted" icon={HandHeart} accent="text-blue-600" />
          <Tile label="Completed" value={rb.completed ?? 0} testId="nw-tile-completed" icon={HandHeart} accent="text-emerald-600" />
          <Tile label="Expired / cancelled" value={(rb.expired ?? 0) + (rb.cancelled ?? 0)} testId="nw-tile-expired" icon={Clock} accent="text-slate-500" />
          <Tile label="Active volunteers" value={summary.volunteers_active} testId="nw-tile-volunteers" icon={Users} accent="text-emerald-600" />
          <Tile label="Paused volunteers" value={summary.volunteers_paused} testId="nw-tile-paused" icon={Users} accent="text-amber-600" />
          <Tile label="Awaiting witness" value={summary.declarations_awaiting_witness} testId="nw-tile-awaiting" icon={AlertTriangle} accent="text-amber-600" />
          <Tile label="Mutual pending email" value={summary.mutual_pending_email_verification} testId="nw-tile-pending-email" icon={MailX} accent="text-amber-600" />
          <Tile label="Email failures" value={summary.email_verification_failures} testId="nw-tile-email-failures" icon={MailX} accent="text-rose-600" />
        </div>
      )}

      <Tabs defaultValue="requests" className="mt-8">
        <TabsList>
          <TabsTrigger value="requests" data-testid="nw-tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="volunteers" data-testid="nw-tab-volunteers">Volunteers</TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          <div className="flex items-center gap-3 mt-4 mb-3">
            <p className="text-xs text-muted-foreground">Status</p>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-52 bg-white" data-testid="nw-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All statuses</SelectItem>
                {Object.keys(REQ_STATUS).map((k) => <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Declaration</TableHead><TableHead>Municipality</TableHead><TableHead>Ward</TableHead>
                <TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead>Expires</TableHead><TableHead>Flags</TableHead>
              </TableRow></TableHeader>
              <TableBody data-testid="nw-requests-table">
                {requests.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <p className="font-mono text-xs font-semibold">{r.declaration_number || "—"}</p>
                      {r.is_test && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[9px] mt-1">ACCEPTANCE TEST DATA</Badge>}
                    </TableCell>
                    <TableCell className="text-sm">{r.municipality_name}</TableCell>
                    <TableCell className="text-xs">{r.ward || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={`${REQ_STATUS[r.request_status] || ""} text-[10px]`}>{r.request_status.replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString("en-CA")}</TableCell>
                    <TableCell className="text-xs">{r.expires_at ? new Date(r.expires_at).toLocaleDateString("en-CA") : "—"}</TableCell>
                    <TableCell className="text-xs">{(r.risk_flags || []).length ? <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-300 text-[9px]">{r.risk_flags.length} flag(s)</Badge> : "—"}</TableCell>
                  </TableRow>
                ))}
                {requests.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No witness requests match this filter.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="volunteers">
          <Card className="mt-4"><CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Volunteer</TableHead><TableHead>Email (masked)</TableHead><TableHead>Municipality</TableHead>
                <TableHead>Ward</TableHead><TableHead>Status</TableHead><TableHead>Verified</TableHead>
                <TableHead>Completed</TableHead><TableHead>Declined</TableHead>
              </TableRow></TableHeader>
              <TableBody data-testid="nw-volunteers-table">
                {volunteers.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="text-sm">{v.name || "—"}{v.is_test && <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-300 text-[9px]">TEST</Badge>}</TableCell>
                    <TableCell className="font-mono text-xs">{v.email_masked}</TableCell>
                    <TableCell className="text-sm">{v.municipality_name}</TableCell>
                    <TableCell className="text-xs">{v.ward || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${v.status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : v.status === "paused" ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-100 text-slate-700 border-slate-300"}`}>{v.status}</Badge></TableCell>
                    <TableCell className="text-xs">{v.email_verified ? "Yes" : "No"}</TableCell>
                    <TableCell className="tabular-nums text-sm">{v.completed_count || 0}</TableCell>
                    <TableCell className="tabular-nums text-sm">{v.declined_count || 0}</TableCell>
                  </TableRow>
                ))}
                {volunteers.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No volunteers yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
